import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * A single screenshot that opens into a full-screen, zoomable lightbox.
 *
 * Used across the site wherever a raw source (terminal screenshot, statement,
 * chart export) needs to be verifiable at full size. The lightbox is rendered
 * through a portal at document level, so the reveal animation's `transform` on
 * any ancestor card can never trap this fixed overlay inside the card.
 *
 * When `src` is empty the `empty` node is rendered instead — that keeps the
 * honest "slot" behaviour for sources we have not published yet.
 */
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;

export interface ZoomableImageProps {
  /** Public path to the real screenshot. Empty renders `empty` instead. */
  src?: string;
  /** Accessible description (used as alt text and the lightbox label). */
  alt: string;
  /** Title shown in the lightbox top bar; falls back to `alt`. */
  title?: string;
  /** Classes for the clickable wrapper (positioning / sizing). */
  className?: string;
  /** Classes for the <img> itself. */
  imageClassName?: string;
  /** Hover hint shown in the top-right of the frame. */
  zoomBadge?: string;
  /** Rendered instead of the image when `src` is empty. */
  empty?: ReactNode;
}

export default function ZoomableImage({
  src,
  alt,
  title,
  className = '',
  imageClassName = '',
  zoomBadge = 'Click to enlarge',
  empty = null,
}: ZoomableImageProps) {
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(MIN_ZOOM);

  const close = useCallback(() => {
    setOpen(false);
    setZoom(MIN_ZOOM);
  }, []);

  const zoomIn = useCallback(() => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2))), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2))), []);

  // Lock page scroll + wire keyboard shortcuts while the lightbox is open.
  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
      else if (event.key === '+' || event.key === '=') zoomIn();
      else if (event.key === '-' || event.key === '_') zoomOut();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close, zoomIn, zoomOut]);

  if (!src) return <>{empty}</>;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Enlarge ${alt}`}
        className={`group block cursor-zoom-in ${className}`}
      >
        <img src={src} alt={alt} draggable={false} className={imageClassName} />
        <span className="absolute right-2.5 top-2.5 inline-flex items-center gap-1.5 rounded-full bg-foreground-950/70 px-2.5 py-1 text-[11px] font-semibold text-background-50 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          <i className="ri-zoom-in-line" aria-hidden="true"></i>
          {zoomBadge}
        </span>
      </button>

      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              role="dialog"
              aria-modal="true"
              aria-label={alt}
              className="fixed inset-0 z-[100] flex flex-col bg-foreground-950/90 backdrop-blur-sm"
            >
              <div className="flex items-center gap-3 border-b border-background-50/15 px-4 py-3">
                <span className="flex items-center gap-1.5" aria-hidden="true">
                  <span className="h-2.5 w-2.5 rounded-full bg-accent-300"></span>
                  <span className="h-2.5 w-2.5 rounded-full bg-secondary-300"></span>
                  <span className="h-2.5 w-2.5 rounded-full bg-primary-300"></span>
                </span>
                <span className="truncate font-num text-xs font-semibold text-background-50">
                  {title ?? alt}
                </span>

                <div className="ml-auto flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={zoomOut}
                    disabled={zoom <= MIN_ZOOM}
                    aria-label="Zoom out"
                    className="flex h-9 w-9 items-center justify-center rounded-md text-background-50 transition-colors hover:bg-background-50/15 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    <i className="ri-zoom-out-line text-lg" aria-hidden="true"></i>
                  </button>
                  <span className="w-12 text-center font-num text-xs font-semibold text-background-50">
                    {Math.round(zoom * 100)}%
                  </span>
                  <button
                    type="button"
                    onClick={zoomIn}
                    disabled={zoom >= MAX_ZOOM}
                    aria-label="Zoom in"
                    className="flex h-9 w-9 items-center justify-center rounded-md text-background-50 transition-colors hover:bg-background-50/15 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    <i className="ri-zoom-in-line text-lg" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => setZoom(MIN_ZOOM)}
                    aria-label="Reset zoom"
                    className="flex h-9 w-9 items-center justify-center rounded-md text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
                  >
                    <i className="ri-restart-line text-lg" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    onClick={close}
                    aria-label="Close"
                    className="ml-1 flex h-9 w-9 items-center justify-center rounded-md bg-background-50/15 text-background-50 transition-colors hover:bg-background-50/25 cursor-pointer"
                  >
                    <i className="ri-close-line text-xl" aria-hidden="true"></i>
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-auto" onClick={close} role="presentation">
                <img
                  src={src}
                  alt={alt}
                  onClick={(event) => event.stopPropagation()}
                  style={{ width: `${zoom * 88}vw` }}
                  className="mx-auto block h-auto max-w-none p-4 md:p-8"
                />
              </div>

              <p className="border-t border-background-50/15 px-4 py-2.5 text-center text-[11px] text-background-50/70">
                Scroll to pan the image · use + / - to zoom · press Esc to close
              </p>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}